import mutations from './config.mutations'
import actions from './config.actions'
import { defaultOf, scopeOf } from '@/config/configSchema'

/**
 * Config Bus — modulo Vuex namespaced `config` (Sprint 16, epic REFACTOR-030).
 *
 * Single source of verità per le preferenze utente/azienda. Registrato come
 * `config: { namespaced: true, ...module }` in `src/store/index.js`.
 *
 * Stato:
 *  - user/company : blob delle chiavi (idratati dai default al load)
 *  - companyId    : azienda corrente per lo scope company
 *  - status       : loading/saving/error per la UX
 *  - dirty        : { user|company: { key: snapshotPrecedente } } per batch+rollback
 *
 * Sprint 16 (Fase 5 integrazione): questo modulo è la single-source-of-truth.
 * Sprint 17-19: migrazione completata — writer via `commitChange` granulare, reader
 * via `value(key)`/`mapConfig`/sotto-chiavi nude; shim e `saveLegacy` ritirati
 * (Sprint 19). Vedi config-bus.md §8.
 */
const state = () => ({
    user: {},
    company: {},
    companyId: null,
    status: {
        loading: false,
        saving: false,
        error: null
    },
    dirty: {
        user: {},
        company: {}
    }
})

const getters = {
    // Lettura granulare per-chiave: tracciando state[scope][key], un consumer che
    // legge questa chiave NON viene invalidato dal cambio di un'altra chiave.
    // Fallback sul default dello schema finché il load non ha idratato lo state.
    value: (state) => (key) => {
        const target = scopeOf(key) === 'company' ? state.company : state.user
        return Object.prototype.hasOwnProperty.call(target, key) ? target[key] : defaultOf(key)
    },

    // Sprint 19: rimossi i getter ponte `blob`/`companyBlob`/`companyValue` (orfani
    // dopo lo sweep dei reader — le eccezioni sanzionate leggono le sotto-chiavi
    // nude dallo state, vedi decision Sprint 19).

    isLoading: (state) => state.status.loading,
    isSaving: (state) => state.status.saving,
    error: (state) => state.status.error,
    hasDirty: (state) =>
        Object.keys(state.dirty.user).length > 0 || Object.keys(state.dirty.company).length > 0
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
